/* Terachat · fundo animado do login: Pulso
 * Traçados de monitor multiparamétrico em câmera lenta; halo lime a cada batimento. Canvas 2D.
 * Componente autocontido, sem dependências além do React.
 * Pausa com a aba oculta, respeita prefers-reduced-motion, limita o DPR e baixa
 * a resolução sozinho se o aparelho não segurar ~40fps.
 *
 * Uso:  <PulsoBackground />   (fixed, cobre a tela, z-index 0; ponha o card por cima)
 */
import { useEffect, useRef, type CSSProperties } from 'react'

const NAVY = '#02172C'
const LIME = '#40D51C'

// ---------- infra ----------
type Frame<S> = (st: S, w: number, h: number, dpr: number, t: number, dt: number) => void

interface LoopOptions<S> {
  maxDpr?: number
  minDpr?: number
  setup?: (st: S, w: number, h: number, dpr: number) => void
  frame: Frame<S>
}

// RAF com: DPR limitado, pausa com aba oculta, quadro estático em reduced-motion
// e queda automática de resolução se a média ficar abaixo de ~40fps.
function runLoop<S>(canvas: HTMLCanvasElement, st: S, { maxDpr = 2, minDpr = 1, setup, frame }: LoopOptions<S>) {
  let dpr = Math.min(window.devicePixelRatio || 1, maxDpr)
  let w = 1, h = 1, raf = 0, last = 0, t = 0, alive = true
  let sum = 0, n = 0
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

  const resize = () => {
    const r = canvas.getBoundingClientRect()
    w = Math.max(1, r.width); h = Math.max(1, r.height)
    canvas.width = Math.max(1, Math.round(w * dpr))
    canvas.height = Math.max(1, Math.round(h * dpr))
    setup?.(st, w, h, dpr)
    if (reduce) frame(st, w, h, dpr, 18, 0)
  }
  const tick = (now: number) => {
    if (!alive) return
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016
    last = now; t += dt
    frame(st, w, h, dpr, t, dt)
    sum += dt; n++
    if (n === 90) {
      const avg = sum / n; sum = 0; n = 0
      if (avg > 1 / 40 && dpr > minDpr) { dpr = Math.max(minDpr, dpr - 0.25); resize() }
    }
    raf = requestAnimationFrame(tick)
  }
  const start = () => { if (!raf && !reduce && alive) { last = 0; raf = requestAnimationFrame(tick) } }
  const stop = () => { cancelAnimationFrame(raf); raf = 0 }
  const onVis = () => (document.hidden ? stop() : start())
  const ro = new ResizeObserver(resize)
  ro.observe(canvas)
  document.addEventListener('visibilitychange', onVis)
  resize()
  start()
  return {
    destroy() { alive = false; stop(); ro.disconnect(); document.removeEventListener('visibilitychange', onVis) },
  }
}

// ---------- efeito ----------
type LaneType = 'ecg' | 'pleth' | 'resp'

interface Lane {
  type: LaneType; main: boolean; depth: number; color: string; prevV: number
  y: number; amp: number
  shape: number   // px/s da forma de onda
  speed: number   // px/s da varredura (shape * SLOW)
  hr: number; rr: number; seed: number
  N: number; buf: Float32Array; k: number; acc: number; gap: number
}

interface PulsoState { lanes: Lane[]; grid: HTMLCanvasElement | null }

const STEP = 2          // px CSS por coluna
const SLOW = 0.45       // câmera lenta: tudo a 45%, mantendo o espaçamento dos batimentos
const BUCKETS = 7
const TYPES: LaneType[] = ['ecg', 'pleth', 'resp', 'ecg', 'pleth', 'ecg', 'resp', 'pleth', 'ecg']
const COLORS: Record<LaneType, string> = { ecg: '#4C8C93', pleth: '#5FA8D3', resp: '#7F93AB' }

const gauss = (x: number, c: number, w: number) => Math.exp(-((x - c) * (x - c)) / (2 * w * w))

function signal(type: LaneType, s: number, lane: Lane): number {
  if (type === 'resp') return Math.sin((2 * Math.PI * s) / lane.rr) * 0.45 + Math.sin(s * 0.7 + lane.seed) * 0.05
  const p = 60 / lane.hr
  const x = (s % p) / p
  if (type === 'pleth') return gauss(x, 0.24, 0.075) * 0.95 + gauss(x, 0.5, 0.07) * 0.32 + x * 0.05 - 0.42
  // ECG: P, Q, R, S, T
  return gauss(x, 0.12, 0.025) * 0.12 - gauss(x, 0.232, 0.008) * 0.14 + gauss(x, 0.25, 0.0085) * 1.0
    - gauss(x, 0.272, 0.01) * 0.26 + gauss(x, 0.46, 0.045) * 0.26 + (Math.random() - 0.5) * 0.015
}

function vignette(ctx: CanvasRenderingContext2D, w: number, h: number, strength: number) {
  const g = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.25, w / 2, h / 2, Math.hypot(w, h) * 0.62)
  g.addColorStop(0, 'rgba(2,23,44,0)')
  g.addColorStop(1, `rgba(1,12,24,${strength})`)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, w, h)
}

export function mountPulso(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}
  let beat = 0 // brilho do batimento principal (0..1)

  const loop = runLoop<PulsoState>(canvas, { lanes: [], grid: null }, {
    maxDpr: 2,
    setup(st, w, h) {
      const count = Math.max(4, Math.min(7, Math.round(h / 140)))
      const laneH = h / count
      const N = Math.ceil(w / STEP) + 2
      const old = st.lanes
      const wide = Math.min(1.4, Math.max(1, w / 700)) // telas largas varrem um pouco mais rápido
      st.lanes = Array.from({ length: count }, (_, i): Lane => {
        const type = TYPES[i % TYPES.length]
        const prev = old[i]
        const main = i === 0 // só a trilha principal leva lime
        const shape = (type === 'resp' ? 70 + Math.random() * 20 : 115 + Math.random() * 45) * wide
        const l: Lane = {
          type, main, depth: main ? 1 : 0.5 + Math.random() * 0.4, color: main ? LIME : COLORS[type], prevV: 0,
          y: laneH * (i + 0.5), amp: laneH * (type === 'ecg' ? 0.36 : 0.26),
          shape, speed: shape * SLOW,
          hr: 64 + Math.random() * 26, rr: 3.6 + Math.random() * 1.6, seed: Math.random() * 10,
          N, buf: new Float32Array(N).fill(NaN), k: 0, acc: 0, gap: 14,
        }
        if (prev && prev.N === N && prev.type === type) {
          // só mudou a resolução (qualidade adaptativa): mantém o traçado
          return { ...l, shape: prev.shape, speed: prev.speed, hr: prev.hr, rr: prev.rr, seed: prev.seed, buf: prev.buf, k: prev.k, acc: prev.acc, depth: prev.depth }
        }
        // já abre com a tela cheia: histórico pré-calculado, cabeça em posição aleatória
        l.k = N + Math.floor(Math.random() * N)
        for (let kk = l.k - (N - l.gap - 1); kk <= l.k; kk++) l.buf[kk % N] = signal(type, (kk * STEP) / l.shape, l)
        return l
      })
      // grade de monitor pré-renderizada
      const g = document.createElement('canvas')
      g.width = canvas.width; g.height = canvas.height
      const gc = g.getContext('2d')
      if (gc) {
        const d = canvas.width / w
        gc.scale(d, d)
        gc.strokeStyle = 'rgba(120,160,200,0.045)'; gc.lineWidth = 1
        gc.beginPath()
        for (let x = 0; x < w; x += 24) { gc.moveTo(x + 0.5, 0); gc.lineTo(x + 0.5, h) }
        for (let y = 0; y < h; y += 24) { gc.moveTo(0, y + 0.5); gc.lineTo(w, y + 0.5) }
        gc.stroke()
        gc.strokeStyle = 'rgba(120,160,200,0.04)'
        gc.setLineDash([2, 6])
        gc.beginPath()
        st.lanes.forEach((l) => { gc.moveTo(0, l.y + l.amp * 0.55); gc.lineTo(w, l.y + l.amp * 0.55) })
        gc.stroke()
      }
      st.grid = g
    },
    frame(st, w, h, dpr, _t, dt) {
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.fillStyle = NAVY
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      if (st.grid) ctx.drawImage(st.grid, 0, 0)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.lineJoin = 'round'; ctx.lineCap = 'round'

      // halo lime atrás do centro (onde fica o card) a cada pico R da trilha principal
      beat *= Math.exp(-(dt || 0) * 2.2)
      if (beat > 0.01) {
        const r = Math.max(w, h) * 0.55
        const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, r)
        g.addColorStop(0, `rgba(64,213,28,${0.06 * beat})`)
        g.addColorStop(0.5, `rgba(64,213,28,${0.02 * beat})`)
        g.addColorStop(1, 'rgba(64,213,28,0)')
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h)
      }

      for (const l of st.lanes) {
        if (dt > 0) {
          l.acc += (l.speed * dt) / STEP
          while (l.acc >= 1) {
            l.acc -= 1; l.k++
            const v = signal(l.type, (l.k * STEP) / l.shape, l)
            if (l.main && v > 0.7 && l.prevV <= 0.7) beat = 1
            l.prevV = v
            l.buf[l.k % l.N] = v
            for (let g = 1; g <= l.gap; g++) l.buf[(l.k + g) % l.N] = NaN // apaga à frente, como monitor real
          }
        }
        const head = l.k % l.N
        const paths = Array.from({ length: BUCKETS }, () => new Path2D())
        for (let i = 1; i < l.N; i++) {
          const a = l.buf[i - 1], b = l.buf[i]
          if (Number.isNaN(a) || Number.isNaN(b) || i - 1 === head) continue
          const age = (head - i + l.N) % l.N
          const p = paths[Math.min(BUCKETS - 1, Math.floor((age / l.N) * BUCKETS))]
          p.moveTo((i - 1) * STEP, l.y - a * l.amp)
          p.lineTo(i * STEP, l.y - b * l.amp)
        }
        // brilho largo só nos dois trechos mais recentes (mais barato que shadowBlur)
        ctx.strokeStyle = l.color
        for (let bk = BUCKETS - 1; bk >= 0; bk--) {
          const f = 1 - bk / BUCKETS
          ctx.globalAlpha = (0.05 + 0.8 * f * f) * l.depth * (l.main ? 1 : 0.55)
          ctx.lineWidth = l.main ? 1.8 : 1.1 + l.depth * 0.4
          ctx.stroke(paths[bk])
          if (bk < 2) {
            ctx.globalAlpha = (l.main ? 0.16 : 0.08) * f * l.depth
            ctx.lineWidth = 6
            ctx.stroke(paths[bk])
          }
        }
        // cabeça do traçado
        const v = l.buf[head]
        if (!Number.isNaN(v)) {
          const hx = head * STEP, hy = l.y - v * l.amp
          const grd = ctx.createRadialGradient(hx, hy, 0, hx, hy, 18)
          grd.addColorStop(0, l.color); grd.addColorStop(1, 'rgba(0,0,0,0)')
          ctx.globalAlpha = l.main ? 0.28 : 0.12; ctx.fillStyle = grd
          ctx.fillRect(hx - 18, hy - 18, 36, 36)
          ctx.globalAlpha = l.main ? 1 : 0.5; ctx.fillStyle = '#EFFFF0'
          ctx.beginPath(); ctx.arc(hx, hy, 1.8, 0, Math.PI * 2); ctx.fill()
        }
      }
      ctx.globalAlpha = 1
      vignette(ctx, w, h, 0.55)
    },
  })
  return () => loop.destroy()
}

// ---------- componente ----------
type Props = { className?: string; style?: CSSProperties }

// Cada montagem cria o próprio <canvas>: assim o double-mount do StrictMode
// não reaproveita um contexto WebGL já destruído.
export default function PulsoBackground({ className, style }: Props) {
  const host = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = host.current
    if (!el) return
    const canvas = document.createElement('canvas')
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block'
    el.appendChild(canvas)
    const destroy = mountPulso(canvas)
    return () => { destroy(); canvas.remove() }
  }, [])

  return (
    <div
      ref={host}
      aria-hidden="true"
      className={className}
      style={{ position: 'fixed', inset: 0, zIndex: 0, background: NAVY, ...style }}
    />
  )
}
